/**
 * This file is responsible for rendering the shopping cart page in the application.
 * It fetches and displays the shopping cart associated with the current user's session.
 *
 * The component uses the 'next/router' library to access the locale from the router.
 * This locale is used to fetch the corresponding translations.
 *
 * The component also handles loading states and errors during the fetching of the shopping cart.
 * If there's an error during the fetch, it is stored in the state and displayed to the user.
 *
 * The ShoppingCart component from 'hah-fe-core' is used to render the shopping cart.
 *
 * The component also checks if there's a cart to reactivate from a previous session.
 * If there is, it attempts to reactivate the cart. If the reactivation fails, an error is displayed.
 */

import { useState, useEffect } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getCart } from '@Hotels-at-Home/hah-fe-core/dist/store/cart/thunks';
import { useShowError } from '@Hotels-at-Home/hah-fe-core/dist/hooks';
import ShoppingCart from '@Hotels-at-Home/hah-fe-core/dist/components/ShoppingCart';
import { HOME } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import { TServerSideProps } from '@Hotels-at-Home/hah-fe-core/dist/types';
import apolloConfig from '~/constants/apolloConfig';

import { useDispatch } from '~/store/hooks';
import {
  isCartSessionValid,
  getReactivateCartSessionId,
  getCartSessionId,
  reactivateCart,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/cart';

import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';

const ShoppingCartPage = () => {
  const { t } = useTranslation('', { keyPrefix: '' });
  const { locale, push } = useRouter();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>();

  const dispatch = useDispatch();
  const showError = useShowError();

  const meta = {
    title: `${t('$local.$meta.meta_title')} - ${t('$common.$headings.shoppingCart')}`,
    description: `${t('$local.$meta.meta_title')} - ${t('$common.$headings.shoppingCart')}`,
  };

  const onMount = async () => {
    let errorMsg;

    setIsLoading(true);

    const reactivateCartSessionId = getReactivateCartSessionId();
    if (reactivateCartSessionId) {
      const reactivateCartStatus = await reactivateCart(reactivateCartSessionId, apolloConfig);
      if (!reactivateCartStatus.success) {
        showError('Error. ReactivateCart server request error');
        if (reactivateCartStatus.isCartIdNotFound) {
          push(HOME);
        }
      }
    }

    const cartSessionId = getCartSessionId();
    if (!cartSessionId) {
      setIsLoading(false);
      return;
    }

    const resultAction = await dispatch(getCart({ cart_id: cartSessionId }));
    if (getCart.rejected.match(resultAction)) {
      errorMsg = resultAction.payload.errorMessage;
    }

    if (errorMsg) {
      setError(errorMsg);
    }

    if (errorMsg && !isCartSessionValid(errorMsg)) {
      push(HOME);
      return;
    }

    setIsLoading(false);
  };

  useEffect(() => {
    onMount();
  }, [locale]);

  return (
    <NextPage error={error} meta={meta}>
      <ShoppingCart isLoading={isLoading} />
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps<TServerSideProps> = async ctx => {
  const props: TServerSideProps = {
    ...(await serverSideTranslations(ctx.locale)),
    error: null,
  };

  return { props };
};

export default ShoppingCartPage;
